<script setup>
import { useI18n } from 'vue-i18n';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';

defineProps({
  catalogEnabled: { type: Boolean, default: false },
  catalogResultLimit: { type: Number, default: 3 },
  catalogImagesOnly: { type: Boolean, default: false },
  catalogUsageInstruction: { type: String, default: '' },
  clientLookupUsageInstruction: { type: String, default: '' },
  isSaving: { type: Boolean, default: false },
});

const emit = defineEmits([
  'update:catalogEnabled',
  'update:catalogResultLimit',
  'update:catalogImagesOnly',
  'update:catalogUsageInstruction',
  'update:clientLookupUsageInstruction',
  'save',
]);

const { t } = useI18n();
</script>

<template>
  <div class="flex flex-col gap-6">
    <div
      class="bg-white dark:bg-n-solid-2 p-6 rounded-xl shadow-sm border border-n-strong"
    >
      <h3 class="text-lg font-semibold text-n-slate-12 mb-2">
        {{ t('LUMERAI_SETTINGS.ABM_SETTINGS.TITLE') }}
      </h3>
      <p class="text-sm text-n-slate-11 mb-6">
        {{ t('LUMERAI_SETTINGS.ABM_SETTINGS.DESCRIPTION') }}
      </p>

      <div class="flex flex-col gap-5">
        <div
          class="flex items-start justify-between gap-4 p-4 bg-n-alpha-1 border border-n-strong rounded-xl"
        >
          <div>
            <h4 class="font-medium text-n-slate-12 m-0">
              {{ t('LUMERAI_SETTINGS.ABM_SETTINGS.CATALOG_TITLE') }}
            </h4>
            <p class="text-sm text-n-slate-11 m-0 mt-1">
              {{ t('LUMERAI_SETTINGS.ABM_SETTINGS.CATALOG_DESCRIPTION') }}
            </p>
          </div>
          <Switch
            :model-value="catalogEnabled"
            :aria-label="t('LUMERAI_SETTINGS.ABM_SETTINGS.CATALOG_TOGGLE')"
            @update:model-value="emit('update:catalogEnabled', $event)"
          />
        </div>

        <template v-if="catalogEnabled">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div class="p-4 bg-n-alpha-1 border border-n-strong rounded-xl">
              <label
                class="block font-medium text-n-slate-12 mb-1"
                for="abm-catalog-result-limit"
              >
                {{ t('LUMERAI_SETTINGS.ABM_SETTINGS.RESULT_LIMIT') }}
              </label>
              <p class="text-sm text-n-slate-11 m-0 mb-3">
                {{ t('LUMERAI_SETTINGS.ABM_SETTINGS.RESULT_LIMIT_HELP') }}
              </p>
              <input
                id="abm-catalog-result-limit"
                :value="catalogResultLimit"
                type="number"
                min="1"
                max="10"
                step="1"
                class="block w-full h-10 px-3 py-2.5 rounded-lg bg-n-alpha-black2 text-sm text-n-slate-12 outline outline-1 outline-n-weak focus:outline-n-brand"
                @input="
                  emit('update:catalogResultLimit', Number($event.target.value))
                "
              />
            </div>

            <div
              class="flex items-center justify-between gap-4 p-4 bg-n-alpha-1 border border-n-strong rounded-xl"
            >
              <div>
                <h4 class="font-medium text-n-slate-12 m-0">
                  {{ t('LUMERAI_SETTINGS.ABM_SETTINGS.IMAGES_ONLY') }}
                </h4>
                <p class="text-sm text-n-slate-11 m-0 mt-1">
                  {{ t('LUMERAI_SETTINGS.ABM_SETTINGS.IMAGES_ONLY_HELP') }}
                </p>
              </div>
              <Switch
                :model-value="catalogImagesOnly"
                @update:model-value="emit('update:catalogImagesOnly', $event)"
              />
            </div>
          </div>

          <div class="p-4 bg-n-alpha-1 border border-n-strong rounded-xl">
            <h4 class="font-medium text-n-slate-12 m-0 mb-1">
              {{ t('LUMERAI_SETTINGS.ABM_SETTINGS.WHEN_TO_SEARCH') }}
            </h4>
            <p class="text-sm text-n-slate-11 m-0 mb-3">
              {{ t('LUMERAI_SETTINGS.ABM_SETTINGS.SEARCH_INSTRUCTION_HELP') }}
            </p>
            <TextArea
              :model-value="catalogUsageInstruction"
              :placeholder="
                t('LUMERAI_SETTINGS.ABM_SETTINGS.SEARCH_PLACEHOLDER')
              "
              :rows="4"
              :max-length="600"
              @update:model-value="
                emit('update:catalogUsageInstruction', $event)
              "
            />
          </div>

          <div class="p-4 bg-n-alpha-1 border border-n-strong rounded-xl">
            <h4 class="font-medium text-n-slate-12 m-0 mb-1">
              {{ t('LUMERAI_SETTINGS.ABM_SETTINGS.WHEN_TO_IDENTIFY') }}
            </h4>
            <p class="text-sm text-n-slate-11 m-0 mb-3">
              {{ t('LUMERAI_SETTINGS.ABM_SETTINGS.IDENTIFY_INSTRUCTION_HELP') }}
            </p>
            <TextArea
              :model-value="clientLookupUsageInstruction"
              :placeholder="
                t('LUMERAI_SETTINGS.ABM_SETTINGS.IDENTIFY_PLACEHOLDER')
              "
              :rows="4"
              :max-length="600"
              @update:model-value="
                emit('update:clientLookupUsageInstruction', $event)
              "
            />
          </div>
        </template>

        <div class="flex justify-end">
          <Button
            :label="t('LUMERAI_SETTINGS.ACTIONS.SAVE_USAGE_RULES')"
            color="blue"
            :is-loading="isSaving"
            @click="emit('save')"
          />
        </div>
      </div>
    </div>
  </div>
</template>
